import config from 'config';
import { differenceInDays } from 'date-fns';
import { DonationOutput, LabworkOutput } from 'gql/types';
import { IAlertConfig } from 'modules/donations/components/CreateDonationAlerts/interface';
import { CreateDonationSchemaType } from 'modules/donations/components/CreateDonationForm/schema';
import { IVitalsParameters } from 'modules/donor/components/UpdateVitalsParametersModal/interface';
import { getTranslation } from 'utils/getTranslation';

const t = getTranslation('donor.CreateDonationAlerts');

//TODO: Separate this function on two, one is for getting errorTypes and second one is for getting the error it self.

export const getDonationAlerts = (
  parameters: IVitalsParameters,
  data: CreateDonationSchemaType,
  previousDonationData?: DonationOutput,
  lastLabwork?: LabworkOutput
): IAlertConfig[] => {
  const examination = data.vitalsSection.examination;
  return [
    {
      message: t('temperatureCheckMessage'),
      rule: () => {
        const isParametersLoaded =
          parameters?.TemperatureToStop?.min &&
          parameters?.TemperatureToStop?.max &&
          parameters?.TemperatureToCheck?.min &&
          parameters?.TemperatureToCheck?.max;

        const temperature = examination?.temperature;

        if (
          !isParametersLoaded ||
          temperature === undefined ||
          temperature === null
        ) {
          return false;
        }

        const minStart = parameters.TemperatureToStop!.min as number;
        const minEnd = parameters.TemperatureToCheck!.min as number;
        const maxStart = parameters.TemperatureToCheck!.max as number;
        const maxEnd = parameters.TemperatureToStop!.max as number;
        return (
          (temperature >= minStart && temperature <= minEnd) ||
          (temperature >= maxStart && temperature <= maxEnd)
        );
      },
      description: t('temperatureCheckDescription'),
    },
    {
      message: t('temperatureStopMessage'),
      rule: () => {
        const isParametersLoaded =
          parameters?.TemperatureToStop?.min &&
          parameters?.TemperatureToStop?.max;
        const temperature = examination?.temperature;

        if (
          !isParametersLoaded ||
          temperature === undefined ||
          temperature === null
        ) {
          return false;
        }

        const min = parameters.TemperatureToStop!.min as number;
        const max = parameters.TemperatureToStop!.max as number;
        return temperature < min || temperature > max;
      },
      description: t('temperatureStopDescription'),
    },
    {
      message: t('heartRateMessage'),
      rule: () => {
        const isParametersLoaded =
          parameters?.Pulse?.min && parameters?.Pulse?.max;
        const pulse = examination?.pulse;

        if (!isParametersLoaded || pulse === undefined || pulse === null) {
          return false;
        }

        const min = parameters.Pulse!.min as number;
        const max = parameters.Pulse!.max as number;
        return pulse < min || pulse > max;
      },
      description: t('heartRateDescription'),
    },
    {
      message: t('pcvMessage'),
      rule: () => {
        const isParametersLoaded = parameters?.PackedCellVolume?.min;
        const packedCellVolume = examination?.packedCellVolume;
        const previousPackedCellVolume = previousDonationData?.packedCellVolume;

        if (
          !isParametersLoaded ||
          packedCellVolume === undefined ||
          packedCellVolume === null
        ) {
          return false;
        }

        const min = parameters.PackedCellVolume!.min as number;
        const max = parameters.PackedCellVolume!.max as number;

        const isNewLower = previousPackedCellVolume
          ? previousPackedCellVolume - packedCellVolume >= 10
          : false;
        return packedCellVolume < min || packedCellVolume > max || isNewLower;
      },
      description:
        previousDonationData?.packedCellVolume &&
        previousDonationData?.packedCellVolume -
          examination?.packedCellVolume >=
          10
          ? t('pcvPrevDescription')
          : t('pcvDescription'),
    },
    {
      message: t('tsMessage'),
      rule: () => {
        const isParameterLoaded = parameters?.TotalSolids?.min;
        const totalSolids = examination?.totalSolids;

        if (
          !isParameterLoaded ||
          totalSolids === undefined ||
          totalSolids === null
        ) {
          return false;
        }

        const min = parameters.TotalSolids!.min as number;
        const max = parameters.TotalSolids!.max as number;
        return totalSolids < min || totalSolids > max;
      },
      description: t('tsDescription'),
    },
    {
      message: t('weightMessage'),
      rule: () => {
        const isParameterLoaded = parameters?.Weight?.percentageDifference;
        const weight = examination?.weight;

        if (
          !isParameterLoaded ||
          !previousDonationData?.weight ||
          weight === undefined ||
          weight === null
        ) {
          return false;
        }

        const percentage = parameters.Weight!.percentageDifference as number;
        return (
          previousDonationData.weight - weight >=
          (previousDonationData.weight / 100) * percentage
        );
      },
      description: t('weightDescription'),
    },
    {
      message: t('overdueLabworkMessage'),
      rule: () => {
        if (lastLabwork) {
          const daysToLastLabwork = Math.abs(
            differenceInDays(new Date(lastLabwork.collectionDate), new Date())
          );

          return (
            daysToLastLabwork > config.VALIDITY_PERIODS.LAST_LABWORK_LIMIT_DAYS
          );
        }
        return false;
      },
      description: t('overdueLabworkDescription'),
    },
    {
      message: t('noLabworkMessage'),
      rule: () => {
        return !lastLabwork;
      },
      description: t('noLabworkDescription'),
    },
  ];
};
